import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  completeTwoFactorChallenge,
} from '../api/authApi';
import { setUnauthorizedHandler } from '../api/client';
import { TOKEN_STORAGE_KEY } from '../api/types';
import { setAppLocale } from '../i18n';

const AuthContext = createContext(undefined);

function readToken() {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

function storeToken(token) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

function clearToken() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

function buildSession(response) {
  return {
    user: response.user,
    user_type: response.user_type,
    roles: response.roles,
    permissions: response.permissions,
    customers: response.customers,
    default_customer_id: response.default_customer_id,
    two_factor_enabled: response.two_factor_enabled ?? response.user?.two_factor_enabled,
  };
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearSession = useCallback(() => {
    clearToken();
    setSession(null);
  }, []);

  const applyAuthResponse = useCallback((response) => {
    storeToken(response.access_token);
    const nextSession = buildSession(response);
    if (nextSession.user?.locale) {
      setAppLocale(nextSession.user.locale);
    }
    setSession(nextSession);
    return nextSession;
  }, []);

  const logout = useCallback(async () => {
    try {
      if (readToken()) {
        await apiLogout();
      }
    } catch {
      // Token may already be invalid.
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const login = useCallback(async (email, password) => {
    const response = await apiLogin(email, password);

    if (response.requires_two_factor) {
      return response;
    }

    return applyAuthResponse(response);
  }, [applyAuthResponse]);

  const completeTwoFactorLogin = useCallback(async (payload) => {
    const response = await completeTwoFactorChallenge(payload);
    return applyAuthResponse(response);
  }, [applyAuthResponse]);

  const refreshSession = useCallback(async () => {
    const data = await fetchMe();
    const nextSession = buildSession(data);
    if (nextSession.user?.locale) {
      setAppLocale(nextSession.user.locale);
    }
    setSession(nextSession);
    return nextSession;
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearSession();
    });
  }, [clearSession]);

  useEffect(() => {
    const token = readToken();

    if (!token) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    fetchMe()
      .then((data) => {
        if (cancelled) {
          return;
        }

        if (!data?.user) {
          clearSession();
          return;
        }

        const nextSession = buildSession(data);
        if (nextSession.user?.locale) {
          setAppLocale(nextSession.user.locale);
        }
        setSession(nextSession);
      })
      .catch(() => {
        if (!cancelled) {
          clearSession();
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  const value = useMemo(
    () => ({
      session,
      isAuthenticated: session !== null,
      isLoading,
      login,
      completeTwoFactorLogin,
      refreshSession,
      logout,
    }),
    [session, isLoading, login, completeTwoFactorLogin, refreshSession, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}
